import { IconButton } from '@nexds/web'
import styled, { css } from 'styled-components'

import { BetaPillContainer, BetaPillLabel } from '../BetaPill'
import {
  DRAWER_DEFAULT_CONTENT_WIDTH,
  DRAWER_EXPANDED_CONTENT_MAX_WIDTH,
  DRAWER_EXPANDED_CONTENT_VIEWPORT_OFFSET,
  DRAWER_MAIN_ITEM_SIZE_STEPS,
  DRAWER_SIDEBAR_SCROLL_MAX_HEIGHT,
  DRAWER_SIDEBAR_SIZE_STEPS
} from './Drawer.constants'
import { DrawerContentWidthVariant } from './Drawer.types'

import { appZIndex } from '@/presentation/utils/zIndexMapper'

/**
 * Medidas da barra lateral, escalonadas por altura de viewport. Ficam no `:root` (via `GlobalStyle`)
 * porque o que se alinha à sidebar mora fora do Drawer também: a caixa de busca e o rodapé de banner.
 */
export const drawerSidebarSizeVariables = css`
  --drawer-option-size: 40px;
  --drawer-option-icon-size: 24px;
  --drawer-option-gap: 8px;
  --drawer-beta-pill-height: 20px;
  --drawer-beta-pill-font-size: 10px;
  --drawer-beta-pill-bottom: -7px;
  /* O botão mais 4px de cada lado (o padding de SideBarGroup) */
  --drawer-sidebar-width: calc(var(--drawer-option-size) + 8px);

  ${DRAWER_SIDEBAR_SIZE_STEPS.map(
    (step) => css`
      @media (max-height: ${step.maxHeight}px) {
        --drawer-option-size: ${step.optionSize}px;
        --drawer-option-icon-size: ${step.iconSize}px;
        --drawer-option-gap: ${step.gap}px;
        --drawer-beta-pill-height: ${step.betaPillHeight}px;
        --drawer-beta-pill-font-size: ${step.betaPillFontSize}px;
        --drawer-beta-pill-bottom: ${step.betaPillBottom}px;
      }
    `
  )}
`

interface DrawerProps {
  isOpen?: boolean
  isActive?: boolean
  isFullscreen?: boolean
  isBeta?: boolean
  $contentWidthVariant?: DrawerContentWidthVariant
}

export const Container = styled.div<DrawerProps>`
  position: absolute;
  z-index: ${({ isFullscreen }) => (isFullscreen ? appZIndex.DRAWER_FULLSCREEN : appZIndex.DRAWER)};
  align-self: center;
  left: ${({ isFullscreen }) => (isFullscreen ? '0' : '16px')};
  height: ${({ isFullscreen }) => (isFullscreen ? '100vh' : '97%')};
  width: ${({ isFullscreen }) => (isFullscreen ? '100vw' : 'fit-content')};

  @media (max-width: 430px) {
    height: 100%;
    left: 0;
    width: ${({ isOpen }) => (isOpen ? '100vw' : '360px')};
  }

  transition:
    left 0.3s ease,
    height 0.3s ease,
    width 0.3s ease;
`
export const LogoContainer = styled.div`
  height: 48px;
  padding: 4px 0;
  display: flex;
  justify-content: center;
  align-items: center;

  & > svg {
    height: 40px;
    display: inline-block;
    transform: translateX(-13.13px);
  }
`

export const MainDrawer = styled.div<DrawerProps>`
  box-shadow: 2px 4px 6px 0px ${({ theme }) => `${theme.colors.secondaryD1}80`};
  position: absolute;
  z-index: ${({ isFullscreen }) => (isFullscreen ? appZIndex.DRAWER_FULLSCREEN : appZIndex.DRAWER)};
  border-right: ${({ isFullscreen }) => (isFullscreen ? '1px solid' : 'none')};
  border-right-color: ${({ theme, isFullscreen }) => (isFullscreen ? theme.colors.secondaryL1 : 'transparent')};
  left: var(--drawer-sidebar-width);
  border-top-right-radius: 20px;
  border-bottom-right-radius: 8px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  width: ${DRAWER_DEFAULT_CONTENT_WIDTH}px;
  padding: 0 ${({ isOpen }) => (isOpen ? '12px' : '0')};
  overflow: hidden;
  background-color: ${({ theme }) => theme.colors.secondaryD1};

  --drawer-main-item-height: 40px;
  --drawer-main-item-margin: 4px;
  --drawer-main-item-font-size: 16px;

  ${DRAWER_MAIN_ITEM_SIZE_STEPS.map(
    (step) => css`
      @media (max-height: ${step.maxHeight}px) {
        --drawer-main-item-height: ${step.itemHeight}px;
        --drawer-main-item-margin: ${step.itemMargin}px;
        --drawer-main-item-font-size: ${step.fontSize}px;
      }
    `
  )}

  ${({ isOpen }) =>
    isOpen
      ? css`
          opacity: 1;
          filter: blur(0px);
          transform: translate3d(0, 0, 0);
          visibility: visible;
          transition: 0.3s all ease;
        `
      : css`
          opacity: 0;
          filter: blur(4px);
          transform: translate3d(-50px, 0, 0);
          visibility: hidden;
          transition: 0.3s all ease;
        `}

  @media (max-width: 430px) {
    width: 100%;
    padding-right: 70px;
    border-radius: 0;
  }
`

interface MainDrawerContentProps {
  $content_position: 'top' | 'bottom'
}

export const MainDrawerContent = styled.div<MainDrawerContentProps>`
  display: flex;
  flex-direction: column;
  /* Só o grupo de cima cede espaço: o de baixo fica colado no rodapé, do tamanho do conteúdo. */
  height: ${({ $content_position }) => ($content_position === 'top' ? '100%' : 'auto')};
  flex: ${({ $content_position }) => ($content_position === 'top' ? '1 1 auto' : '0 0 auto')};
  width: 100%;
  min-height: 0;
  justify-content: ${({ $content_position }) => ($content_position === 'top' ? 'start' : 'end')};
  align-items: center;

  /* Rede de segurança: abaixo do menor degrau nem os itens reduzidos cabem. */
  @media (max-height: ${DRAWER_SIDEBAR_SCROLL_MAX_HEIGHT}px) {
    overflow-y: ${({ $content_position }) => ($content_position === 'top' ? 'auto' : 'visible')};
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }
`

export const MainDrawerItem = styled.button<DrawerProps>`
  border: none;
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: flex;
  align-items: center;
  height: var(--drawer-main-item-height);
  flex-shrink: 0;
  width: 100%;
  padding: 0 16px;
  margin: var(--drawer-main-item-margin);
  border-radius: 50px;
  background-color: ${({ theme, isActive }) => (isActive ? `${theme.colors.primary}59` : theme.colors.secondaryD1)};
  color: ${({ theme, isActive }) => (isActive ? theme.colors.primaryL2 : theme.colors.neutralL5)};
  ${({ theme }) => theme.fonts.infoLG};
  font-size: var(--drawer-main-item-font-size);

  &:hover {
    background-color: ${({ theme }) => theme.colors.secondary};
  }
`

export const OptionContainer = styled.div<DrawerProps>`
  box-shadow: 2px 4px 6px 0px
    ${({ theme }) => `${theme.colors.secondaryD1}80`}${({ isBeta, theme }) =>
      isBeta ? `, inset 0 0 0 2px ${theme.colors.yellowL0}` : ''};
  ${({ isBeta }) =>
    isBeta &&
    css`
      &::after {
        content: 'FUNCIONALIDADE EM BETA';
        position: absolute;
        bottom: 0;
        left: 50%;
        transform: translateX(-50%);
        background-color: ${({ theme }) => theme.colors.yellowL0};
        color: ${({ theme }) => theme.colors.secondaryD3};
        ${({ theme }) => theme.fonts.labelSM};
        padding: 2px 6px;
        z-index: ${appZIndex.DRAWER + 2};
      }
    `}

  position: absolute;
  z-index: ${({ isFullscreen }) => (isFullscreen ? appZIndex.DRAWER_FULLSCREEN : appZIndex.DRAWER)};

  border-top-right-radius: ${({ isFullscreen }) => (isFullscreen ? '0' : '20px')};
  border-bottom-right-radius: ${({ isFullscreen }) => (isFullscreen ? '0' : '8px')};

  left: var(--drawer-sidebar-width);
  height: 100%;
  width: ${({ isFullscreen, $contentWidthVariant }) => {
    if (isFullscreen) {
      return 'calc(100% - var(--drawer-sidebar-width))'
    }

    if ($contentWidthVariant === 'expanded') {
      return `min(${DRAWER_EXPANDED_CONTENT_MAX_WIDTH}px, calc(100vw - ${DRAWER_EXPANDED_CONTENT_VIEWPORT_OFFSET}px))`
    }

    return `${DRAWER_DEFAULT_CONTENT_WIDTH}px`
  }};
  background-color: ${({ theme }) => theme.colors.secondaryD1};
  padding: 0px 12px 8px 12px;

  transition:
    border-top-right-radius 0.3s ease,
    border-bottom-right-radius 0.3s ease,
    width 0.3s ease;

  ${({ isOpen }) =>
    isOpen
      ? css`
          opacity: 1;
          filter: blur(0px);
          transform: translate3d(0, 0, 0);
          visibility: visible;
          transition: 0.3s all ease;
        `
      : css`
          opacity: 0;
          filter: blur(4px);
          transform: translate3d(-50px, 0, 0);
          visibility: hidden;
          transition: 0.3s all ease;
        `}

  @media (max-width: 430px) {
    width: 100%;
    padding-right: 70px;
    border-radius: 0;
  }
`

export const OptionButtonWrapper = styled.div``

interface OptionButtonProps {
  isActive?: boolean
}

export const OptionButton = styled(IconButton)<OptionButtonProps>`
  position: relative;
  background-color: ${({ theme, isActive }) => (isActive ? `${theme.colors.primary}59` : theme.colors.secondaryD1)};
  width: var(--drawer-option-size);
  height: var(--drawer-option-size);

  & svg {
    width: var(--drawer-option-icon-size);
    height: var(--drawer-option-icon-size);
  }

  @media (hover: none) {
    &:focus {
      background-color: ${({ theme, isActive }) =>
        isActive ? `${theme.colors.primary}79` : theme.colors.secondary} !important;
    }
  }

  @media (hover: hover) {
    &:hover {
      background-color: ${({ theme, isActive }) =>
        isActive ? `${theme.colors.primary}79` : theme.colors.secondary} !important;
    }
  }
`

interface SidebarProps {
  hasBorder: boolean
  isFullscreen?: boolean
}

export const SideBar = styled.div<SidebarProps>`
  box-shadow: 2px 4px 6px 0px ${({ theme }) => `${theme.colors.secondaryD1}80`};
  z-index: ${appZIndex.DRAWER + 1};
  background-color: ${({ theme }) => theme.colors.secondaryD1};
  border-top-right-radius: ${({ hasBorder }) => (hasBorder ? 0 : '20px')};
  border-bottom-right-radius: ${({ hasBorder }) => (hasBorder ? 0 : '8px')};
  border-top-left-radius: ${({ isFullscreen }) => (isFullscreen ? '0' : '8px')};
  border-bottom-left-radius: ${({ isFullscreen }) => (isFullscreen ? '0' : '8px')};
  border-right: 1px solid ${({ theme, hasBorder }) => (hasBorder ? theme.colors.secondaryL1 : 'transparent')};
  position: absolute;
  width: var(--drawer-sidebar-width);
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;

  transition:
    border-top-right-radius 0.4s ease,
    border-bottom-right-radius 0.4s ease,
    border-top-left-radius 0.3s ease,
    border-bottom-left-radius 0.3s ease;

  ${BetaPillContainer} {
    width: var(--drawer-option-size);
    height: var(--drawer-beta-pill-height);
    padding: 0;
  }

  ${BetaPillLabel} {
    margin: 0;
    font-size: var(--drawer-beta-pill-font-size);
    line-height: var(--drawer-beta-pill-font-size);
    letter-spacing: calc(var(--drawer-beta-pill-font-size) / 10);
  }

  & > *:first-child {
    width: var(--drawer-option-size);
    height: var(--drawer-option-size);
    margin: 4px;
    flex-shrink: 0;
  }

  @media (max-height: 430px) {
    padding-bottom: 8px;
  }

  @media (max-width: 430px) {
    border-radius: 0;
  }
`

export const SideBarContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  height: 100%;
  width: 100%;
  min-height: 0;
  border-top: 1px solid ${({ theme }) => theme.colors.secondaryL1};

  /* Rede de segurança: abaixo do menor degrau nem os botões reduzidos cabem. */
  @media (max-height: ${DRAWER_SIDEBAR_SCROLL_MAX_HEIGHT}px) {
    overflow-y: auto;
    gap: 8px;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }
`

export const SideBarGroup = styled.div`
  display: flex;
  flex-direction: column;
  padding: 4px;
  gap: var(--drawer-option-gap);
  flex-shrink: 0;
`

export const NotificationDot = styled.span`
  position: absolute;
  top: 6px;
  right: 6px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.notification};
  /* border: 2px solid ${({ theme }) => theme.colors.secondaryD1}; */
  box-shadow: -3px 2px 4px ${({ theme }) => theme.colors.secondaryD3};
  box-sizing: content-box;
`
